<template>
  <div class="product-details-ai-prompts-wrapper">
    <img
      v-if="showAskBrandHeader && hasGooglePrompts"
      class="ask-brand-header"
      src="https://services.google.com/fh/files/misc/y2ej-title.png"
      alt="Ask Macy's"
    />

    <ScrollableContainer :class="{ 'margin-top-xs': !isSwitchButtonAvailable }" :hide-arrows="askButtonOnNextRow">
      <div class="product-details-ai-prompts" :class="{ 'max-width-100': askButtonOnNextRow }">
        <ViewDetailsButton v-if="viewDetailsText" :text="viewDetailsText" :hide="!isSwitchButtonAvailable" @click="$emit('view-full-details')" />

        <div v-if="viewDetailsText" class="divider-icon" :class="{ hide: !isSwitchButtonAvailable || !promptsAvailable }"></div>

        <div v-if="hasGooglePrompts" class="google-prompts" :class="{ 'max-width-100': askButtonOnNextRow }">
          <!-- <MockGoogleAiPrompts v-if="useMockGooglePrompts" direction="row" limit="3" /> -->
          <google-ai-prompts direction="row" limit="3">
            <CustomPromptButton :is-agent-loaded="isAgentLoaded" :is-agent-skeleton="isAgentSkeleton" :has-google-prompts="hasGooglePrompts" />
          </google-ai-prompts>
        </div>

        <CustomPromptButton v-else :is-agent-loaded="isAgentLoaded" :is-agent-skeleton="isAgentSkeleton" :has-google-prompts="hasGooglePrompts" />
      </div>
    </ScrollableContainer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, onBeforeMount } from 'vue';
import ScrollableContainer from '~/components/common/ScrollableContainer.vue';
import ViewDetailsButton from './ViewDetailsButton.vue';
import CustomPromptButton from './CustomPromptButton.vue';
import { fireLinkTag } from '~/features/analytics/tagManager';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
// import MockGoogleAiPrompts from './MockGoogleAiPrompts.vue';

defineProps({
  viewDetailsText: {
    type: String,
  },
  isSwitchButtonAvailable: {
    type: Boolean,
    default: false,
  },
  askButtonOnNextRow: {
    type: Boolean,
    default: false,
  },
  showAskBrandHeader: {
    type: Boolean,
    default: true,
  },
});

const { killswitches } = useKillswitches();

defineEmits(['view-full-details']);

const SKELETON_TIMEOUT = 30000;
const hasGooglePrompts = ref(true);
const isAgentLoaded = ref(false);
const isAgentSkeleton = ref(true);
let loadedPrompts = [];
let googlePromptTagFired = false;

// const useMockGooglePrompts = computed(() => process.env.NODE_ENV === 'development');

const promptsAvailable = computed(() => hasGooglePrompts.value || isAgentLoaded.value || isAgentSkeleton.value);

const handlePromptsLoaded = (e) => {
  loadedPrompts = e.detail?.prompts || [];
  hasGooglePrompts.value = loadedPrompts.length > 0;
};

const handlePromptsUnavailable = () => {
  hasGooglePrompts.value = false;
};

const handlePromptsClicked = () => {
  const tagName = 'ask ai:open chat:product details:suggestion';

  if (killswitches.featureEnabledF || !googlePromptTagFired) {
    fireLinkTag({
      event_name: tagName,
      link_name: tagName,
    });
    googlePromptTagFired = true;
  }
};

const handleAgentLoaded = () => {
  isAgentLoaded.value = true;
  isAgentSkeleton.value = false;
};

onBeforeMount(() => {
  window.addEventListener('google-agent-pdp-prompts-loaded', handlePromptsLoaded);
  window.addEventListener('google-agent-pdp-prompts-unavailable', handlePromptsUnavailable);
  window.addEventListener('google-agent-pdp-prompt-clicked', handlePromptsClicked);
  window.addEventListener('google-agent-loaded', handleAgentLoaded);

  if (window.googleAgent) {
    handleAgentLoaded();
  }
});

onMounted(() => {
  setTimeout(() => {
    if (isAgentSkeleton.value) {
      isAgentSkeleton.value = false;
    }
  }, SKELETON_TIMEOUT);
});

onBeforeUnmount(() => {
  window.removeEventListener('google-agent-pdp-prompts-loaded', handlePromptsLoaded);
  window.removeEventListener('google-agent-pdp-prompts-unavailable', handlePromptsUnavailable);
  window.removeEventListener('google-agent-pdp-prompt-clicked', handlePromptsClicked);
  window.removeEventListener('google-agent-loaded', handleAgentLoaded);
});
</script>

<style lang="scss" scoped brand="mcom">
@import '~settingSCSS';

.ask-brand-header {
  display: block;
  height: rem-calc(20);
  width: auto;
  margin-bottom: rem-calc(8);
}

.product-details-ai-prompts {
  display: flex;
  align-items: center;
  gap: 8px;
}

google-ai-prompts {
  --google-ai-prompt-border-radius: 110px;
  --google-ai-prompt-padding: 8.5px 16px 8.5px 8px;
  --google-ai-prompt-font-weight: 400;
  --google-ai-prompt-background-color: #{$background-color};
  --google-ai-prompt-color: #{$black};
  --google-ai-prompt-border-color: #{$gray-2-color};
  --google-ai-prompt-justify-content: center;
  --google-ai-prompt-align-items: center;
  --google-ai-prompt-icon-url: url(https://services.google.com/fh/files/misc/y2ej-spark-graphic.png);
  --google-ai-prompt-icon-size: 16px;
  --google-ai-prompt-icon-display: inline-block;
  --google-ai-prompt-gap: 8px;
  --google-ai-prompt-flex: 0 0 auto;
  --google-ai-prompts-flex-wrap: nowrap;
  --google-ai-prompts-margin: 0;
  --google-ai-prompts-mobile-direction: row;
  --google-ai-prompt-min-width: 150px;
}

.divider-icon {
  display: inline-block;
  width: rem-calc(1);
  height: rem-calc(16);
  background-size: contain;
  background-repeat: no-repeat;
  margin: 0 8px;
  padding: 0 1px;
}

.google-prompts {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
  width: max-content;
}

.max-width-100 {
  max-width: 100%;
}
</style>
